<template>
  <div
    class="container v-content-container tooltip-wrapper"
    :key="componentKey"
  >
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-list-alt"></i>
          <span v-if="selectedEvent != null">
            {{ selectedEvent.name }}
          </span>
          <router-link
            class="btn btn-v-lightgreen-white pull-right"
            :to="{ name: 'NewEventPrice' }"
            v-tooltip="{
              content: $label('event.create_new_price', 'Create new price'),
              classes: 'custom-tooltip'
            }"
          >
            <i class="fa fa-fw fa-lg fa-plus"></i>
          </router-link>
        </h1>
      </div>
    </div>

    <navigation-tabs :event-id="selectedEventId"></navigation-tabs>

    <div class="row mt-8">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div class="row mt-8">
        <!--BEGIN Default Price-->
        <div class="col-md-6 col-sm-12">
          <div v-show="!defaultPriceLoading" class="v-form-partial">
            <div class="v-form">
              <fieldset>
                <div class="row">
                  <section class="col col-6">
                    <label class="input">{{
                      $label("event.stock_price", "Stock price")
                    }}</label>
                  </section>
                  <!--BEGIN Price usable online-->
                  <section class="col col-6">
                    <label class="checkbox">
                      <input
                        type="checkbox"
                        @change="
                          defaultPrice.usable_online = !defaultPrice.usable_online
                        "
                        id="price-usable-online"
                        name="usable_online"
                        :checked="defaultPrice.usable_online"
                      /><i></i> View online
                    </label>
                  </section>
                  <!--END Price usable online-->
                </div>
                <div class="row">
                  <!-- BEGIN Price form -->
                  <section class="col col-3">
                    <label class="label">{{
                      $label("event.price_input", "input")
                    }}</label>

                    <label class="select">
                      <select
                        id="price-form"
                        :disabled="updating"
                        v-model="defaultPrice.form"
                      >
                        <option value="net">Net</option>
                        <option value="gross">Gross</option>
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('form')" class="text-danger">
                      {{ getError("form") }}
                    </p>
                  </section>
                  <!-- END Price form -->
                  <!-- BEGIN Price VAT-->
                  <section class="col col-3">
                    <label class="label">{{
                      $label("event.price_vat", "VAT")
                    }}</label>

                    <label class="select">
                      <select
                        id="price-vat"
                        :disabled="updating"
                        v-model="defaultPrice.vat_id"
                      >
                        <option
                          v-for="vat in availableVats"
                          :value="vat.id"
                          :key="vat.id"
                          >{{ vat.name }}</option
                        >
                      </select>
                      <i></i>
                    </label>

                    <p v-if="hasError('vat')" class="text-danger">
                      {{ getError("vat") }}
                    </p>
                  </section>
                  <!-- END Price USt -->
                  <!-- BEGIN Price amount-->
                  <section class="col col-3">
                    <text-input
                      v-model="defaultPrice.amount"
                      id="price-amount"
                      :label="$label('event.course_price', 'Course price')"
                      :placeholder="$label('event.course_price', '0')"
                      :is-disabled="updating"
                      :has-error="hasError('amount')"
                      :error="getError('amount')"
                      @keyup.native.enter="updateEventPrice"
                    />
                  </section>
                  <!-- END Price amount -->

                  <section class="col col-3">
                    <info-button
                      class="default-price-button mt-6"
                      :class="{ 'is-disabled': updating }"
                      id="save-default-price-button"
                      :text="$label('common.save', 'Save')"
                      @click.native="createOrUpdatePrice"
                    >
                    </info-button>
                  </section>
                </div>
              </fieldset>
            </div>
          </div>
          <div v-show="defaultPriceLoading" class="row">
            <div class="col-xs-12">
              <view-loader
                margin-top-pixels="15"
                border-color="primary"
              ></view-loader>
            </div>
          </div>
        </div>
        <!--END Default Price-->

        <!--BEGIN Activate/Deactivate freebie-->
        <div class="col-md-6 col-sm-12 activate-price-alert">
          <alert-box
            class="activate-price-alert"
            v-if="!isFreebie && !eventLoading"
            type="info"
            id="alert-deactivate-price"
            :closeable="false"
            :show-icon="true"
          >
            {{
              $label(
                "event.free_course",
                "No invoices are issued for free courses"
              )
            }}
            <info-button
              class="btn-xs pull-right activate-button"
              :class="{ 'is-disabled': updating }"
              id="deactivate-prices-button"
              :text="$label('event.deactivate_price', 'Deactivate prices')"
              @click.native="toggleEventFreebie"
            >
            </info-button>
          </alert-box>
          <alert-box
            class="activate-price-alert"
            v-if="isFreebie && !eventLoading"
            type="success"
            id="alert-activate-price"
            :closeable="false"
            :show-icon="true"
          >
            {{
              $label(
                "event.free_prices_successfully",
                "The event prices are free"
              )
            }}
            <success-button
              class="btn-xs pull-right activate-button"
              :class="{ 'is-disabled': updating }"
              id="activate-prices-button"
              :text="$label('event.deactivate_price', 'Activate prices')"
              @click.native="toggleEventFreebie"
            >
            </success-button>
          </alert-box>
          <div v-show="eventLoading" class="row">
            <div class="col-xs-12">
              <view-loader
                margin-top-pixels="15"
                border-color="primary"
              ></view-loader>
            </div>
          </div>
        </div>
        <!--END Activate/Deactivate freebie-->
      </div>

      <hr />

      <div class="row mb-2">
        <div class="col-sm-6 col-xs-12" style="display: flex;">
          <select class="pagination-select" id="per-page" v-model="perPage">
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="20">20</option>
          </select>
          <div id="action-dropdown" class="btn-group" style="margin-left: 5px;">
            <button
              type="button"
              class="btn btn-primary dropdown-toggle"
              data-toggle="dropdown"
            >
              <i class="fa fa-refresh"></i> &nbsp;
              {{ $label("common.actions", "Actions") }} &nbsp;<span
                class="caret"
              ></span>
            </button>
            <ul class="dropdown-menu" role="menu">
              <li>
                <router-link
                  :to="{
                    name: 'NewEventPrice',
                    params: { event_id: selectedEventId }
                  }"
                  ><i class="text-primary fa fa-fw fa-lg fa-plus"></i>
                  {{
                    $label("event.create_new_price", "Create new price")
                  }}</router-link
                >
              </li>
            </ul>
          </div>
        </div>
        <div
          v-show="
            eventPricesLength > 0 && eventPricesLength != null && !failedRequest
          "
          class="col-sm-6 col-xs-12 hidden-xs"
        >
          <div
            class="dataTables_paginate paging_bootstrap_full_number"
            style="text-align: right;"
          >
            <ul class="pagination mt-0" style="visibility: visible;">
              <li class="prev" :class="{ disabled: disabledFirstPage }">
                <a
                  @click="goToFirstPage"
                  v-tooltip="{
                    content: $label('common.first_page', 'First page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-left"></i
                ></a>
              </li>
              <li class="prev" :class="{ disabled: disabledPreviousPage }">
                <a
                  @click="goToPreviousPage"
                  v-tooltip="{
                    content: $label('common.back'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-left"></i
                ></a>
              </li>
              <li class="active">
                <a>{{ currentPage }}</a>
              </li>
              <li class="next" :class="{ disabled: disabledNextPage }">
                <a
                  @click="goToNextPage"
                  v-tooltip="{
                    content: $label('common.next', 'Next'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-right"></i
                ></a>
              </li>
              <li class="next" :class="{ disabled: disabledLastPage }">
                <a
                  @click="goToLastPage"
                  v-tooltip="{
                    content: $label('common.last_page', 'Last page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-right"></i
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row mt-8">
        <div class="col-xs-12">&nbsp;</div>
      </div>

      <alert-box
        v-show="eventPricesLength < 1 && eventPricesLength != null"
        type="warning"
        id="alert-no-prices"
        :closeable="false"
        :show-icon="false"
      >
        {{ $label("event.no_price_exist", "No prices exist.") }}
      </alert-box>

      <alert-box
        v-show="failedRequest"
        type="warning"
        :closeable="false"
        :show-icon="false"
      >
        {{
          $label(
            "event.event_failed_request",
            "Request failed while loading event prices."
          )
        }}
      </alert-box>

      <div
        v-show="
          eventPricesLength > 0 && eventPricesLength != null && !failedRequest
        "
        class="row"
      >
        <div class="col-xs-12">
          <div class="popup-wrapper">
            <div class="table-responsive">
              <fieldset>
                <table
                  class="table dataTable table-striped table-bordered table-hover mb-1"
                  id="table-staff"
                >
                  <thead>
                    <tr>
                      <th class="text-center">
                        {{ $label("common.type", "Type") }}
                      </th>
                      <th class="text-center">
                        {{ $label("common.gross", "Gross") }}
                      </th>
                      <th class="text-center">
                        {{ $label("common.vat", "VAT") }}
                      </th>
                      <th class="text-center">
                        {{ $label("common.designation", "designation") }}
                      </th>
                      <th class="text-center table-settings-staff-action">
                        {{ $label("common.actions", "Actions") }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="price in eventPrices" v-bind:key="price.id">
                      <td
                        class="v-align-center text-center"
                        :id="'price-type-' + price.id"
                      >
                        {{ price.type }}
                      </td>
                      <td
                        class="v-align-center text-center"
                        :id="'price-gross-' + price.id"
                      >
                        {{ price.amount_gross }} EUR
                      </td>
                      <td
                        class="v-align-center text-center"
                        :id="'price-vat-' + price.id"
                      >
                        {{ price.vat.name }}
                      </td>
                      <td
                        class="v-align-center text-center"
                        :id="'price-name-' + price.id"
                      >
                        {{ price.name }}
                      </td>
                      <td class="text-center min-width-auto">
                        <div class="btn-group">
                          <router-link
                            :to="{
                              name: 'EventPriceDetails',
                              params: {
                                event_id: selectedEventId,
                                price_id: price.id
                              }
                            }"
                            data-id="$id"
                            class="btn btn-sm btn-default"
                            ><i class="text-primary fa fa-fw fa-eye"></i>
                            {{ $label("common.details", "Details") }}
                          </router-link>
                          <a
                            @click="deleteEventPrice(price.id)"
                            :id="'delete-price-button-' + price.id"
                            class="btn btn-sm btn-danger"
                            ><i class="fa fa-fw fa-trash"></i>
                            {{ $label("common.delete", "Delete") }}</a
                          >
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </fieldset>
            </div>
          </div>
        </div>
      </div>

      <div
        v-show="
          eventPricesLength > 0 && eventPricesLength != null && !failedRequest
        "
        class="row"
      >
        <div class="col-sm-6 col-xs-12 dataTables_info">
          {{ from }} - {{ to }} {{ $label("common.pagination_of", "of") }}
          {{ total }}
        </div>
        <div class="col-sm-6 col-xs-12">
          <div
            class="dataTables_paginate paging_bootstrap_full_number"
            style="text-align: right;"
          >
            <ul class="pagination" style="visibility: visible;">
              <li class="prev" :class="{ disabled: disabledFirstPage }">
                <a
                  @click="goToFirstPage"
                  v-tooltip="{
                    content: $label('common.first_page', 'First page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-left"></i
                ></a>
              </li>
              <li class="prev" :class="{ disabled: disabledPreviousPage }">
                <a
                  @click="goToPreviousPage"
                  v-tooltip="{
                    content: $label('common.back'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-left"></i
                ></a>
              </li>
              <li class="active">
                <a>{{ currentPage }}</a>
              </li>
              <li class="next" :class="{ disabled: disabledNextPage }">
                <a
                  @click="goToNextPage"
                  v-tooltip="{
                    content: $label('common.next', 'Next'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-right"></i
                ></a>
              </li>
              <li class="next" :class="{ disabled: disabledLastPage }">
                <a
                  @click="goToLastPage"
                  v-tooltip="{
                    content: $label('common.last_page', 'Last page'),
                    classes: 'custom-tooltip'
                  }"
                  ><i class="fa fa-angle-double-right"></i
                ></a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div class="row mb-8">
        <div class="col-xs-12">&nbsp;</div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import swal from "sweetalert";
import FormErrorsMixin from "@/mixins/FormErrors";

import NavigationTabs from "@/components/Settings/Events/EventDetails/NavigationTabs.vue";
import ViewLoader from "@/components/Common/ViewLoader.vue";
import AlertBox from "@/components/Common/Alerts/AlertBox";
import TextInput from "@/components/Common/Inputs/TextInput.vue";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import SuccessButton from "@/components/Common/Buttons/SuccessButton";
import Error404 from "@/components/Common/Error404";

export default {
  name: "EventPrices.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    TextInput,
    InfoButton,
    SuccessButton,
    AlertBox,
    Error404
  },

  data() {
    return {
      eventPrices: [],
      loading: false,
      noDataExist: false,
      defaultPriceLoading: false,
      eventLoading: false,
      updating: false,
      eventPricesLength: null,
      defaultPrice: {
        name: "Default",
        form: "gross",
        usable_online: false,
        vat_id: 1,
        amount: 0
      },
      availableVats: null,
      isFreebie: false,
      failedRequest: false,
      currentPage: 1,
      total: 0,
      from: 0,
      to: 0,
      firstPage: 1,
      lastPage: null,
      perPage: 20,
      selectedEvent: null,
      selectedEventId: null,
      componentKey: 0,
      validatedInputs: ["form", "vat", "amount"]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedEventId = vm.$route.params.event_id;

      if (
        vm.$route.query.event_price_deleted === true ||
        vm.$route.query.event_price_deleted === "true"
      ) {
        swal("Success!", "The price was deleted.", "success").then(() => {
          window.history.pushState(
            {},
            document.title,
            window.location.pathname
          );
        });
      }
      vm.getEventPrices(() => {
        //
      });
      vm.getEvent(() => {
        //
      });
      vm.getDefaultPrice(() => {
        //
      });
      vm.getVats(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    if (to.query.event_price_deleted === true) {
      swal("Success!", "The price was deleted.", "success").then(() => {
        window.history.pushState({}, document.title, window.location.pathname);
      });
    }
    this.forceRerender();
    if (this.to % this.perPage === 1 && this.currentPage !== 1) {
      this.currentPage -= 1;
    }
    this.getEventPrices(() => {
      //
    });
    this.getEvent(() => {
      //
    });
    this.getDefaultPrice(() => {
      //
    });
    this.getVats(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdatePrice() {
      if (!this.defaultPrice.id) {
        this.createEventPrice();
      } else {
        this.updateEventPrice();
      }
    },

    getEvent() {
      this.resetErrors();
      this.eventLoading = true;
      const endpoint = this.$partnerEndpoint("events/" + this.selectedEventId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedEvent = response.data.data;
          this.isFreebie = response.data.data.freebie;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.eventLoading = false;
        });
    },

    getEventPrices(successCallback) {
      if (this.loading) {
        return;
      }

      this.loading = true;
      this.eventPrices = [];
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices"
      );

      Api()
        .get(endpoint, {
          params: { page: this.currentPage, perPage: this.perPage }
        })
        .then(response => {
          this.eventPrices = response.data.data;
          this.currentPage = response.data.meta.current_page;
          this.total = response.data.meta.total;
          if (response.data.meta.from != null) {
            this.from = response.data.meta.from;
          } else {
            this.from = 0;
          }
          if (response.data.meta.to != null) {
            this.to = response.data.meta.to;
          } else {
            this.to = 0;
          }
          this.lastPage = response.data.meta.last_page;
          this.perPage = response.data.meta.per_page;
          this.eventPricesLength = this.eventPrices.length;
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    deleteEventPrice(priceId) {
      if (this.loading || this.updating || priceId == null) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The price will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "events/" + this.selectedEventId + "/prices/" + priceId
          );

          Api()
            .delete(endpoint)
            .then(() => {
              this.$router
                .push({
                  name: "EventPrices",
                  query: { event_price_deleted: true }
                })
                .catch(error => {
                  console.log(error);
                  if (
                    error.name === "NavigationDuplicated" ||
                    error.message.includes(
                      "Avoided redundant navigation to current location"
                    )
                  ) {
                    swal("Success!", "The price was deleted.", "success");
                    this.getEventPrices(() => {
                      //
                    });
                  }
                });
            })
            .catch(error => {
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete event prices.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    createEventPrice() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices"
      );

      Api()
        .post(endpoint, this.defaultPrice)
        .then(() => {
          this.getDefaultPrice();
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    updateEventPrice() {
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices/" + this.defaultPrice.id
      );

      Api()
        .put(endpoint, this.defaultPrice)
        .then(() => {
          this.getDefaultPrice();
        })
        .catch(error => {
          console.log(error);
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    getDefaultPrice() {
      this.resetErrors();
      this.defaultPriceLoading = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/prices/default"
      );

      Api()
        .get(endpoint)
        .then(response => {
          this.defaultPrice.id = response.data.data.id;
          this.defaultPrice.name = response.data.data.name;
          this.defaultPrice.description = response.data.data.description;
          this.defaultPrice.type = response.data.data.type;
          this.defaultPrice.usable_online = response.data.data.usable_online;
          this.defaultPrice.form = response.data.data.form;
          this.defaultPrice.amount =
            response.data.data.form === "net"
              ? response.data.data.amount_net
              : response.data.data.amount_gross;
          this.defaultPrice.vat_id = response.data.data.vat.id;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.defaultPriceLoading = false;
        });
    },

    getVats() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("vats");

      Api()
        .get(endpoint)
        .then(response => {
          this.availableVats = response.data.data;
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    toggleEventFreebie() {
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/freebie"
      );

      Api()
        .put(endpoint)
        .then(() => {
          this.getEvent();
        })
        .catch(error => {
          console.log(error);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    goToFirstPage() {
      if (!this.disabledFirstPage) {
        this.currentPage = 1;
        this.getEventPrices(() => {
          //
        });
      }
    },

    goToPreviousPage() {
      if (!this.disabledPreviousPage) {
        this.currentPage--;
        this.getEventPrices(() => {
          //
        });
      }
    },

    goToNextPage() {
      if (!this.disabledNextPage) {
        this.currentPage++;
        this.getEventPrices(() => {
          //
        });
      }
    },

    goToLastPage() {
      if (!this.disabledLastPage) {
        this.currentPage = this.lastPage;
        this.getEventPrices(() => {
          //
        });
      }
    },

    forceRerender() {
      this.componentKey += 1;
    }
  },

  computed: {
    disabledFirstPage() {
      return this.currentPage === 1;
    },

    disabledPreviousPage() {
      return this.currentPage === 1;
    },

    disabledNextPage() {
      return this.currentPage === this.lastPage;
    },

    disabledLastPage() {
      return this.currentPage === this.lastPage;
    }
  },
  watch: {
    perPage() {
      this.getEventPrices();
    }
  }
};
</script>

<style scoped>
.mt-0 {
  margin-top: 0;
}

.mt-8 {
  margin-top: 1.3rem;
}

.mb-0 {
  margin-bottom: 0;
}

.mb-1 {
  margin-bottom: 0.3rem;
}

.mb-2 {
  margin-bottom: 0.6rem;
}

.mb-8 {
  margin-bottom: 1.3rem;
}

.min-width-auto {
  width: 50px;
}

.pagination-select {
  font-weight: 700;
  height: 34px;
  padding-left: 10px;
  width: 70px;
  cursor: pointer;
  border: 1px solid #ddd;
  color: #666;
  outline: none;
}

.default-price-button {
  padding: 6px !important;
  margin-top: 2.5rem;
}

.activate-button {
  margin-top: 0;
}

@media screen and (max-width: 600px) {
  .default-price-button {
    margin-top: 0;
  }
  .activate-button {
    margin-top: 1rem;
    width: 100%;
  }
  .activate-price-alert {
    margin-top: 1.3rem;
  }
  .activate-price-alert > div {
    display: flex;
    text-align: center;
  }
}
</style>
